import { ChevronDown, Send } from 'lucide-react';
import { Fragment } from 'react';

import { START_OUTREACH_MAX_SHIFT_COUNT } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import { dynamicWidgetBindingProps } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_dom';
import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import { type DynamicWidgetMetricTags } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import {
    type DynamicWidgetIssue,
    type ShiftListWidgetModel,
    type ShiftListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    actionButtonDisabled,
    actionButtonLabel,
    type ShiftListDateFilter,
    shiftReferences,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_view_model';
import {
    TEMPLATE_CARD_FOOTER_ACTIONS_CLASS,
    TEMPLATE_CARD_FOOTER_CLASS,
    TEMPLATE_CARD_HEADER_CLASS,
    TEMPLATE_CARD_TITLE_CLASS,
    TEMPLATE_FILTER_CHIP_CLASS,
    TEMPLATE_PRIMARY_BUTTON_SM_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { type RenderedShiftActionState } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/use_shift_widget_actions';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import {
    SegmentedControl,
    type SegmentedControlOption,
} from '@apps/web/components/ui/segmented_control';
import { Spinner } from '@apps/web/components/ui/spinner';
import { cn } from '@apps/web/lib/utils';

const ALL_FILTER_VALUE = '__all__';

const DATE_FILTER_OPTIONS: ReadonlyArray<
    SegmentedControlOption<ShiftListDateFilter>
> = [
    { value: 'all', label: 'All' },
    { value: 'today', label: 'Today' },
    { value: 'next_week', label: 'Next week' },
];

function ShiftListFilterChip({
    label,
    value,
    options,
    onValueChange,
}: {
    readonly label: string;
    readonly value: string | null;
    readonly options: ReadonlyArray<string>;
    readonly onValueChange: (value: string | null) => void;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={TEMPLATE_FILTER_CHIP_CLASS}
                >
                    <span className="min-w-0 truncate">
                        {label}: {value ?? 'all'}
                    </span>
                    <ChevronDown className="size-3" aria-hidden="true" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                <DropdownMenuRadioGroup
                    value={value ?? ALL_FILTER_VALUE}
                    onValueChange={(nextValue) =>
                        onValueChange(
                            nextValue === ALL_FILTER_VALUE ? null : nextValue,
                        )
                    }
                >
                    <DropdownMenuRadioItem value={ALL_FILTER_VALUE}>
                        All
                    </DropdownMenuRadioItem>
                    {options.map((option) => (
                        <DropdownMenuRadioItem key={option} value={option}>
                            {option}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function ShiftListFilters({
    dateFilter,
    clientFilter,
    locationFilter,
    clientOptions,
    locationOptions,
    onDateFilterChange,
    onClientFilterChange,
    onLocationFilterChange,
}: {
    readonly dateFilter: ShiftListDateFilter;
    readonly clientFilter: string | null;
    readonly locationFilter: string | null;
    readonly clientOptions: ReadonlyArray<string>;
    readonly locationOptions: ReadonlyArray<string>;
    readonly onDateFilterChange: (value: ShiftListDateFilter) => void;
    readonly onClientFilterChange: (value: string | null) => void;
    readonly onLocationFilterChange: (value: string | null) => void;
}) {
    return (
        <Fragment>
            <div className="px-3.5 pt-2.5">
                <SegmentedControl
                    options={DATE_FILTER_OPTIONS}
                    value={dateFilter}
                    onValueChange={onDateFilterChange}
                    layout="auto"
                    size="sm"
                    aria-label="Filter shifts by date"
                />
            </div>
            {clientOptions.length > 1 || locationOptions.length > 1 ? (
                <div className="flex flex-wrap gap-1.5 px-3.5 pt-2">
                    {clientOptions.length > 1 ? (
                        <ShiftListFilterChip
                            label="Client"
                            value={clientFilter}
                            options={clientOptions}
                            onValueChange={onClientFilterChange}
                        />
                    ) : null}
                    {locationOptions.length > 1 ? (
                        <ShiftListFilterChip
                            label="Location"
                            value={locationFilter}
                            options={locationOptions}
                            onValueChange={onLocationFilterChange}
                        />
                    ) : null}
                </div>
            ) : null}
        </Fragment>
    );
}

export function ShiftIssueSection({
    issues,
    metricTags,
    placement,
}: {
    readonly issues: ReadonlyArray<DynamicWidgetIssue>;
    readonly metricTags: DynamicWidgetMetricTags;
    readonly placement: 'widget' | 'content';
}) {
    if (issues.length === 0) {
        return null;
    }
    const notices = issues.map((issue) => (
        <DynamicWidgetIssueNotice
            key={issue.id}
            issue={issue}
            metricTags={metricTags}
        />
    ));
    if (placement === 'widget') {
        return notices;
    }
    return <div className="space-y-1.5 px-3.5 pt-2.5">{notices}</div>;
}

export function ShiftListHeader({
    model,
}: {
    readonly model: ShiftListWidgetModel;
}) {
    return (
        <div className={TEMPLATE_CARD_HEADER_CLASS}>
            <p
                className={TEMPLATE_CARD_TITLE_CLASS}
                {...dynamicWidgetBindingProps(model.widgetId, 'rowCount')}
            >
                {model.rowCount} {model.rowCount === 1 ? 'shift' : 'shifts'}
            </p>
        </div>
    );
}

export function ShiftListFooter({
    model,
    hiddenCount,
    totalRowCount,
    hasMoreRows,
    isLoadingMoreRows,
    allowBulkAction,
    bulkRows,
    bulkActionState,
    bulkActionIssue,
    bulkModelActionIssues,
    metricTags,
    actionsEnabled,
    onShowAll,
    onRequestOutreach,
}: {
    readonly model: ShiftListWidgetModel;
    readonly hiddenCount: number;
    readonly totalRowCount: number;
    readonly hasMoreRows: boolean;
    readonly isLoadingMoreRows: boolean;
    readonly allowBulkAction: boolean;
    readonly bulkRows: ReadonlyArray<ShiftListWidgetRow>;
    readonly bulkActionState: RenderedShiftActionState;
    readonly bulkActionIssue: DynamicWidgetIssue | null;
    readonly bulkModelActionIssues: ReadonlyArray<DynamicWidgetIssue>;
    readonly metricTags: DynamicWidgetMetricTags;
    readonly actionsEnabled: boolean;
    readonly onShowAll: () => void;
    readonly onRequestOutreach: (
        rows: ReadonlyArray<ShiftListWidgetRow>,
        displayText: string,
    ) => Promise<void>;
}) {
    const bulkActionExceedsLimit =
        model.bulkShiftIds.length > START_OUTREACH_MAX_SHIFT_COUNT;
    return (
        <div className={TEMPLATE_CARD_FOOTER_CLASS}>
            {hiddenCount > 0 || hasMoreRows ? (
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={isLoadingMoreRows}
                    onClick={onShowAll}
                >
                    {isLoadingMoreRows ? <Spinner size="xs" /> : null}
                    {isLoadingMoreRows
                        ? 'Loading shifts'
                        : `Show all ${totalRowCount} shifts`}
                </Button>
            ) : null}
            {model.note ? (
                <p
                    className="text-muted-foreground min-w-0 text-xs break-words"
                    {...dynamicWidgetBindingProps(model.widgetId, 'note')}
                >
                    {model.note}
                </p>
            ) : null}
            {allowBulkAction &&
            model.bulkOpenShiftCount >= 2 &&
            actionsEnabled ? (
                <div className={TEMPLATE_CARD_FOOTER_ACTIONS_CLASS}>
                    <Button
                        type="button"
                        size="sm"
                        {...dynamicWidgetBindingProps(
                            model.widgetId,
                            'bulkShiftIds',
                        )}
                        disabled={
                            model.bulkShiftIds.length === 0 ||
                            bulkActionExceedsLimit ||
                            actionButtonDisabled(
                                bulkActionState.state,
                                bulkActionState.isSubmitting,
                            )
                        }
                        title={
                            bulkActionExceedsLimit
                                ? `One outreach supports up to ${START_OUTREACH_MAX_SHIFT_COUNT} shifts.`
                                : undefined
                        }
                        onClick={() =>
                            void onRequestOutreach(
                                bulkRows,
                                `Create one coverage outreach for ${shiftReferences(
                                    model.bulkShiftIds,
                                )}`,
                            )
                        }
                        className={cn(
                            TEMPLATE_PRIMARY_BUTTON_SM_CLASS,
                            'max-w-full text-center whitespace-normal',
                        )}
                    >
                        {bulkActionState.isSubmitting ? (
                            <Spinner size="xs" />
                        ) : (
                            <Send className="size-3.5" />
                        )}
                        {bulkActionState.isSubmitting
                            ? 'Requesting outreach'
                            : bulkActionExceedsLimit
                              ? `Split into groups of ${START_OUTREACH_MAX_SHIFT_COUNT}`
                              : actionButtonLabel(
                                    bulkActionState.state,
                                    `Start outreach for all ${model.bulkOpenShiftCount} open`,
                                )}
                    </Button>
                    {bulkActionIssue !== null ? (
                        <DynamicWidgetIssueNotice
                            issue={bulkActionIssue}
                            metricTags={metricTags}
                        />
                    ) : null}
                    {bulkModelActionIssues.map((issue) => (
                        <DynamicWidgetIssueNotice
                            key={issue.id}
                            issue={issue}
                            metricTags={metricTags}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
